import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchEntries, IContentfulFilters } from 'api/contentful/fetchEntries';
import { flattenContentfulData, FlatEntry } from 'services/cms/flattenContentful';
import { Status } from 'services/cms/typesUtil';
import { get } from 'utils/EntryUtil';
import { isValidLocale } from 'utils/Locales';
import { BooleanFilter } from 'utils/TypeUtils';

export interface IUseContentful<T> {
  query: string;
  status: Status;
  data?: Array<FlatEntry<T>>;
  message?: string;
}

/**
 * Hook that gets CMS-agnostic data from Contentful
 * @param filters Contentful filters
 * @returns flattened Contentful data with response
 * @example
 *   const items = useContentful<Bundle>({
 *     contentType: ContentType.BUNDLE,
 *     filters: { fields: { dcsId: 'FULL_SERVICE_DINING_BEST' } },
 *    });
 */
export function useContentful<T extends object>(
  filters?: IContentfulFilters<T>,
): IUseContentful<T> | undefined {
  const [items, setItems] = useState<IUseContentful<T> | undefined>();
  const { language } = useTranslation().i18n;

  const fetchData = async () => {
    /** Do nothing if no language or contentType */
    if (!language || !filters || !filters.contentType || !isValidLocale(language)) {
      if (items) setItems(undefined);
      return;
    }

    const query = JSON.stringify(filters);

    /** Do nothing if the request is same as current data */
    if (query === items?.query) return;

    setItems({
      query,
      status: Status.PENDING,
    });

    try {
      const entries = await fetchEntries<T>(language, filters);
      const items = get(entries, 'items', []);
      const data = items.map(flattenContentfulData).filter(BooleanFilter) as Array<FlatEntry<T>>;

      setItems({
        query,
        status: Status.RESOLVED,
        data,
      });
    } catch (err) {
      const message = `Contentful Response for ${filters.contentType} not available: ${err.message}`;

      setItems({
        query,
        status: Status.ERRORED,
        message,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [language, filters]);

  if (items?.status === Status.PENDING) return undefined;

  return items;
}
